// Expenditure category editor — renders Fixed and Variable groups with progress bars.
import React from "react";
import { Card, NumberInput, ProgressBar } from "./primitives";
import { CATEGORY_COLORS, fmtCAD } from "../lib/budget";

const SHARE_OVER_THRESHOLD = 40;

const GROUP_STYLES = {
  fixed: { background: "#5F716122", color: "#5F7161" },
  variable: { background: "#E27D6022", color: "#B8593E" },
};

const ExpenseRow = ({ cat, value, income, onChange, limit }) => {
  const share = income > 0 ? (value / income) * 100 : 0;
  const over = share > SHARE_OVER_THRESHOLD;
  const cap = Number(limit) || 0;
  const capPct = cap > 0 ? (value / cap) * 100 : 0;
  let capBadge = null;
  if (cap > 0) {
    if (capPct >= 100) {
      capBadge = <span data-testid={`limit-flag-${cat}`} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E27D60] text-white whitespace-nowrap">over {fmtCAD(cap)} cap</span>;
    } else if (capPct >= 80) {
      capBadge = <span data-testid={`limit-flag-${cat}`} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#E2B35B] text-[#1A1C1A] whitespace-nowrap">{Math.round(capPct)}% of {fmtCAD(cap)} cap</span>;
    } else {
      capBadge = <span data-testid={`limit-flag-${cat}`} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#EDF3E0] text-[#3D5B0F] whitespace-nowrap">cap {fmtCAD(cap)}</span>;
    }
  }
  return (
    <div data-testid={`row-${cat}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-center">
      <div className="sm:col-span-3 flex items-center gap-2 flex-wrap">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[cat] }} />
        <span className="text-[15px] font-medium text-[#1A1C1A]">{cat}</span>
        {capBadge}
      </div>
      <div className="sm:col-span-3">
        <NumberInput value={value} onChange={(val) => onChange(cat, val)} testId={`input-${cat}`} />
      </div>
      <div className="sm:col-span-6">
        <div className="flex items-center justify-between text-xs text-[#5A5C58] mb-1.5">
          <span className="font-mono">{share.toFixed(1)}% of income</span>
          <span className="font-mono">{fmtCAD(value)}</span>
        </div>
        <ProgressBar
          value={value}
          max={income > 0 ? income : value || 1}
          color={CATEGORY_COLORS[cat]}
          over={over}
        />
      </div>
    </div>
  );
};

const ExpenseGroup = ({ group, expenses, income, onChange, spendLimits }) => (
  <div data-testid={`group-${group.key}`} className="mb-7 last:mb-0">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full"
          style={GROUP_STYLES[group.key]}
        >
          {group.badge}
        </span>
        <h4 className="font-display text-base font-semibold text-[#1A1C1A]">{group.title}</h4>
      </div>
      <span data-testid={`group-${group.key}-total`} className="text-sm font-mono text-[#5A5C58]">
        {fmtCAD(group.total)}
        {income > 0 && (
          <span className="ml-2 text-[#8A8C88]">
            ({Math.round((group.total / income) * 100)}%)
          </span>
        )}
      </span>
    </div>
    <p className="text-xs text-[#5A5C58] mb-3 -mt-2">{group.subtitle}</p>

    <div className="space-y-4">
      {group.cats.map((cat) => (
        <ExpenseRow
          key={cat}
          cat={cat}
          value={Number(expenses[cat]) || 0}
          income={income}
          onChange={onChange}
          limit={spendLimits ? spendLimits[cat] : 0}
        />
      ))}
    </div>
  </div>
);

export default function ExpenseEditor({
  income,
  totalExpenses,
  expenses,
  fixedCats,
  variableCats,
  fixedTotal,
  variableTotal,
  onChange,
  spendLimits = {},
}) {
  const groups = [
    { key: "fixed", title: "Fixed Expenses", subtitle: "Recurring monthly commitments", cats: fixedCats, total: fixedTotal, badge: "FIXED" },
    { key: "variable", title: "Variable Expenses", subtitle: "Lifestyle spending — adjust to hit your goal", cats: variableCats, total: variableTotal, badge: "VARIABLE" },
  ];
  const incomePct = income > 0 ? Math.round((totalExpenses / income) * 100) : null;

  return (
    <Card className="md:col-span-7 p-6 sm:p-8" testId="card-expenses" delay={0}>
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <h3 className="font-display text-xl font-semibold">Expenditure Categories</h3>
          <p className="text-sm text-[#5A5C58]">Edit each line. Progress shows share of income.</p>
        </div>
        <span className="text-sm font-mono text-[#5A5C58]">
          {incomePct === null ? "—" : `${incomePct}% of income`}
        </span>
      </div>

      {groups.map((group) => (
        <ExpenseGroup
          key={group.key}
          group={group}
          expenses={expenses}
          income={income}
          onChange={onChange}
          spendLimits={spendLimits}
        />
      ))}
    </Card>
  );
}
